import {
  mapEntity,
  ScenariosCondition,
  ScenariosRequest,
  ScenariosResponse,
  scenario,
} from '@api';
import { getTimeTillEndOfDayMs, RangeHorizon } from '@helpers';
import { QueryClient, queryOptions } from '@tanstack/react-query';
import { config } from '@terminal/config';
import { withGcTime } from '@terminal/services/query-client/utils';
import {
  ApiStreamProps,
  apiStream,
} from '@terminal/utils/api-stream/apiStream';
import { formatScenarioConditionValue } from '@terminal/utils/condition-utils/condition-utils';

import { createEntityQueryOptions } from '../entities/useEntities.utils';
import type { ScenariosData } from './useScenarios';

export const QUERY_KEY_SCENARIOS = 'QUERY_KEY_SCENARIOS';
export const SCENARIO_HORIZONS: string[] = [
  RangeHorizon.OneWeek,
  RangeHorizon.TwoWeeks,
  RangeHorizon.OneMonth,
  RangeHorizon.ThreeMonths,
  RangeHorizon.SixMonths,
  RangeHorizon.OneYear,
];

export const createScenarioRequest = ({
  conditions,
  entityTags,
}: {
  conditions: ScenariosCondition[];
  entityTags: string[];
}): ScenariosRequest => ({
  entities: entityTags.map(tag => ({ tag })),
  conditions: conditions.map(formatScenarioConditionValue),
  horizons: SCENARIO_HORIZONS,
  start_horizon: RangeHorizon.ZeroDay,
  end_horizon: RangeHorizon.OneYear,
  skip_report: false,
  non_overlapping: true,
});

interface CreateScenariosQueryOptionsProps {
  body: ScenariosRequest;
  queryClient: QueryClient;
  gcTime?: number;
}

export const createScenariosQueryKey = (body: ScenariosRequest) =>
  [
    QUERY_KEY_SCENARIOS,
    {
      ...body,
      ...(body.conditions
        ? {
            conditions: body.conditions.map(
              ({ display: _display, ...condition }) => condition
            ),
          }
        : {}),
    },
  ] as const;

const mapScenarioChunks = async ({
  chunks,
  queryClient,
}: {
  chunks: ScenariosResponse[];
  queryClient: QueryClient;
}) => {
  const entities = await Promise.all(
    chunks.map(chunk =>
      queryClient.fetchQuery(createEntityQueryOptions(chunk.entity))
    )
  );

  return entities.reduce<ScenariosData[]>((result, entity, index) => {
    if (entity.data) {
      result.push({
        ...chunks[index],
        entity: mapEntity(entity.data),
      });
    }

    return result;
  }, []);
};

export const createScenariosQueryOptions = ({
  body,
  queryClient,
  gcTime,
}: CreateScenariosQueryOptionsProps) => {
  const queryKey = createScenariosQueryKey(body);

  return withGcTime(
    queryOptions({
      queryKey,
      queryFn: ({ signal }) =>
        new Promise<ScenariosData[]>((resolve, reject) => {
          const controller = new AbortController();
          let latestData: ScenariosData[] = [];

          const handleAbort = () => controller.abort();
          signal.addEventListener('abort', handleAbort, { once: true });

          const cleanup = () => {
            signal.removeEventListener('abort', handleAbort);
          };

          const streamProps: ApiStreamProps<ScenariosResponse> = {
            body,
            onReadChunk: async chunks => {
              const nextData = await mapScenarioChunks({ chunks, queryClient });
              signal.throwIfAborted();
              latestData = [...latestData, ...nextData];
              queryClient.setQueryData(queryKey, latestData);
            },
            onComplete: () => {
              cleanup();
              resolve(latestData);
            },
            onError: error => {
              cleanup();
              reject(error ?? new Error('Scenario request failed'));
            },
            onAbort: () => {
              cleanup();
              reject(
                new DOMException('Scenario request aborted', 'AbortError')
              );
            },
            controller,
            endpoint: `${config.api.root}${scenario.scenarios.path}`,
          };

          apiStream(streamProps);
        }),
      staleTime: getTimeTillEndOfDayMs(),
    }),
    gcTime
  );
};
